import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ShieldCheck, Send } from "lucide-react";
import { useState } from "react";

export default function DeliverabilityTips() {
  const [tipsOpen, setTipsOpen] = useState(false);
  const [resendOpen, setResendOpen] = useState(false);

  return (
    <div className="space-y-3">
      <Collapsible open={resendOpen} onOpenChange={setResendOpen} className="border border-border bg-card rounded-lg shadow-sm overflow-hidden">
        <CollapsibleTrigger className="flex items-center justify-between w-full p-4 hover:bg-muted/50 transition-colors">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Send className="w-5 h-5 text-primary" />
            How sending works
          </div>
          <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${resendOpen ? "rotate-180" : ""}`} />
        </CollapsibleTrigger>
        <CollapsibleContent className="border-t border-border p-4 bg-muted/20">
          <p className="text-sm text-muted-foreground mb-3">
            MailCraft sends every email through our verified shared sender. There is nothing
            for you to configure or maintain &mdash; no SMTP credentials, no DNS records,
            no app passwords.
          </p>
          <ul className="text-sm text-muted-foreground space-y-2 list-disc list-inside ml-2 marker:text-primary/60">
            <li>
              The <strong>From</strong> address is owned and authenticated by MailCraft
              (SPF, DKIM, and DMARC are already in place).
            </li>
            <li>
              You control the friendly <strong>From name</strong> below &mdash; that is
              what recipients see in their inbox before the email address.
            </li>
            <li>
              Replies are routed to your <strong>Support Email</strong> if set in the
              footer section, otherwise back to the From address.
            </li>
            <li>
              Per-account hourly send limits keep the shared sender's reputation healthy
              for everyone. Check the dashboard for your remaining quota.
            </li>
          </ul>
        </CollapsibleContent>
      </Collapsible>

      <Collapsible open={tipsOpen} onOpenChange={setTipsOpen} className="border border-border bg-card rounded-lg shadow-sm overflow-hidden">
        <CollapsibleTrigger className="flex items-center justify-between w-full p-4 hover:bg-muted/50 transition-colors">
          <div className="flex items-center gap-2 text-sm font-medium">
            <ShieldCheck className="w-5 h-5 text-primary" />
            Email Deliverability Tips
          </div>
          <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${tipsOpen ? "rotate-180" : ""}`} />
        </CollapsibleTrigger>
        <CollapsibleContent className="border-t border-border p-4 bg-muted/20">
          <ul className="text-sm text-muted-foreground space-y-2 list-disc list-inside ml-4 marker:text-primary/50">
            <li><strong>Pick a clear From name:</strong> Use your real brand or product name so recipients recognise the email at a glance.</li>
            <li><strong>Avoid spammy phrasing:</strong> Keep subjects clean. Avoid excessive exclamation marks or words like "FREE", "URGENT", or ALL CAPS.</li>
            <li><strong>Provide a clean unsubscribe:</strong> Include an unsubscribe link in lifecycle emails. Transactional emails usually don't need one, but respect user preferences.</li>
            <li><strong>Set a reachable Support Email:</strong> Recipients who reply or report issues will land at this address.</li>
            <li><strong>Test with real inboxes:</strong> Send a draft to Gmail, Outlook, and Yahoo to confirm rendering and inbox placement.</li>
          </ul>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
